import { useSimpleEventSubscription } from '@passionware/simple-event';
import { ReactNode, useRef } from 'react';
import { flushSync } from 'react-dom';
import { cn } from 'v5/platform/dom/cn';
import type { AnySpec, KindMap } from 'v5/core/form-schema/formKinds.types';
import type {
  AnyFieldSpecBase,
  EditUnit,
  ScopeSection
} from 'v5/core/form-schema/formSchema.types';
import { useExclusiveEdit } from 'v5/patterns/forms/exclusive-edit/ExclusiveEditContext';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import { ListUnitView } from './ListUnitView';
import type {
  SchemaSectionsProps,
  UnitBinding,
  UnitBindings
} from './schemaForm.types';
import { ScopeSectionView } from './ScopeSectionView';
import { SectionTabs } from './SectionTabs';
import { UnitForm } from './UnitForm';
import { NO_UNIT_FOLD } from './unitFold';
import { isUnitOrEntry } from './unitPath';
import {
  SECTION_SCROLL_MARGIN_CLASS,
  sectionElement,
  useSectionNavigation
} from './useSectionNavigation';

export interface SchemaSectionsRenderProps<M extends KindMap<M>>
  extends SchemaSectionsProps<M> {
  /** Icon per section (workspace icon); `null` for none. */
  renderSectionIcon?(section: ScopeSection<AnyFieldSpecBase>): ReactNode;
  /** Icon before a unit's title (what kind of value it holds); `null` for none. */
  renderUnitIcon?(unit: EditUnit<AnySpec<M>>): ReactNode;
}

/**
 * Edit mode root: scope sections → units → fields, one RHF per unit.
 * Bindings are looked up by unit id; a unit without a binding is a
 * programming error and throws early. Units take turns in edit mode through
 * the host's `ExclusiveEditProvider`, which decides how a discard is
 * confirmed. Spans the host's scroll area edge to edge — or scrolls its
 * sections itself below the tab bar (`scroll="own"`) — and spaces itself
 * (`SCHEMA_FORM_GUTTER`).
 */
export function SchemaSections<M extends KindMap<M>>({
  schema,
  kit,
  bindings,
  variant,
  size,
  navigation = 'none',
  scroll = 'host',
  singleSectionHeader = true,
  editUnitSignal,
  unitFold = NO_UNIT_FOLD,
  renderSectionIcon,
  renderUnitIcon
}: SchemaSectionsRenderProps<M>) {
  const exclusiveEdit = useExclusiveEdit();
  const singleSection = schema.sections.length === 1;
  const ownScroll = scroll === 'own';
  // In a scroll area of its own nothing covers its top: a section is the one
  // in view once its top is about where the first one starts (`pt-6`).
  const sectionNav = useSectionNavigation(
    schema.sections,
    ownScroll ? 32 : undefined
  );
  const tabs = navigation === 'tabs' && !singleSection;

  // Units hear `editUnitSignal` themselves. One in a collapsed section is not
  // mounted to hear it: the section opens at once, and the target goes out
  // again to its units, listening by then. That second emit reaches this
  // listener too, and passes it by.
  const reemittingRef = useRef(false);
  useSimpleEventSubscription(editUnitSignal?.addListener, target => {
    if (reemittingRef.current) return;
    const section = schema.sections.find(candidate =>
      candidate.units.some(unit => isUnitOrEntry(target.unitId, unit.id))
    );
    if (!section || singleSection || sectionNav.isOpen(section)) return;
    flushSync(() => sectionNav.setOpen(section.id, true));
    reemittingRef.current = true;
    try {
      editUnitSignal?.emit(target);
    } finally {
      reemittingRef.current = false;
    }
  });

  function setSectionOpen(sectionId: string, open: boolean) {
    const root = sectionNav.rootRef.current;
    const element = root && sectionElement(root, sectionId);
    if (open || !element) {
      sectionNav.setOpen(sectionId, open);
      return;
    }
    // Collapsing unmounts the section's units: the one in edit mode lets go first.
    exclusiveEdit.releaseWithin(element, () =>
      sectionNav.setOpen(sectionId, false)
    );
  }

  const tabBar = tabs && (
    <SectionTabs
      sections={schema.sections}
      activeId={sectionNav.activeId}
      onSelect={sectionNav.goTo}
      renderIcon={renderSectionIcon}
    />
  );
  const sectionViews = (
    <div ref={sectionNav.rootRef} className="flex flex-col gap-6">
      {schema.sections.map(section => (
        <ScopeSectionView
          key={section.id}
          section={section}
          icon={renderSectionIcon ? renderSectionIcon(section) : null}
          alwaysOpen={singleSection}
          showHeader={!singleSection || singleSectionHeader}
          open={sectionNav.isOpen(section)}
          onOpenChange={open => setSectionOpen(section.id, open)}
          className={
            !tabs
              ? undefined
              : ownScroll
                ? 'scroll-mt-6'
                : SECTION_SCROLL_MARGIN_CLASS
          }
        >
          {/* The one room between units, filled or empty: a unit at rest
              is only as high as its title and value (`compactTitleRow`). */}
          <div className="flex flex-col gap-3.5">
            {section.emptyNote && section.units.length === 0 && (
              <p className="text-sm text-fg-muted">{section.emptyNote}</p>
            )}
            {section.units.map(unit => {
              const binding = bindingFor(bindings, unit);
              if (unit.type === 'list') {
                if (binding.type !== 'list') {
                  throw new Error(
                    `Unit ${unit.id} is a list but its binding is '${binding.type}'`
                  );
                }
                return (
                  <ListUnitView
                    key={unit.id}
                    unit={unit}
                    binding={binding}
                    kit={kit}
                    variant={variant}
                    size={size}
                    foldSource={unitFold}
                    editUnitSignal={editUnitSignal}
                    icon={renderUnitIcon?.(unit) ?? null}
                  />
                );
              }
              if (binding.type !== 'fields') {
                throw new Error(
                  `Unit ${unit.id} is a fields unit but its binding is '${binding.type}'`
                );
              }
              return (
                <UnitForm
                  key={unit.id}
                  unit={unit}
                  values={binding.values}
                  onSave={binding.save}
                  clear={binding.clear}
                  overriddenIn={binding.overriddenIn}
                  kit={kit}
                  variant={variant}
                  size={size}
                  editUnitSignal={editUnitSignal}
                  foldSource={unitFold}
                  icon={renderUnitIcon?.(unit) ?? null}
                />
              );
            })}
          </div>
        </ScopeSectionView>
      ))}
    </div>
  );

  if (ownScroll) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {tabBar}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <ScrollShadowWrapper
            renderTopShadow={false}
            renderBottomShadow={false}
            className="min-h-0 flex-1 with-styled-scrollbars with-styled-scrollbars__transparent"
          >
            <div className={cn('pb-3', tabs ? 'pt-6' : 'pt-3')}>
              {sectionViews}
            </div>
          </ScrollShadowWrapper>
          {/* The edge shadows lie over the scroll area, its scrollbar
              included, so they run the full width; the scroll area's own
              would stop at the scrollbar. Above the sticky rows inside it. */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 z-20 h-1.5 border-t border-border-default bg-gradient-to-b from-black/5 to-transparent opacity-0 transition-opacity duration-100 [[data-has-top-shadow]~&]:opacity-100"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-1.5 border-b border-border-default bg-gradient-to-t from-black/5 to-transparent opacity-0 transition-opacity duration-100 [[data-has-bottom-shadow]~&]:opacity-100"
          />
        </div>
      </div>
    );
  }
  return (
    // The tab bar brings its own top padding; without it the form pads itself.
    <div className={cn('flex flex-col gap-6 pb-3', !tabs && 'pt-3')}>
      {tabBar}
      {sectionViews}
    </div>
  );
}

function bindingFor<M extends KindMap<M>>(
  bindings: UnitBindings<M>,
  unit: EditUnit<AnySpec<M>>
): UnitBinding<M> {
  const binding = bindings[unit.id];
  if (!binding) {
    throw new Error(`Missing binding for unit ${unit.id}`);
  }
  return binding;
}
